import { ArrowLeft, Info, Save, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { useInvalidateOrg, usePolicies } from "../api/queries";
import type { Policy, PolicyInput } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { retentionReach, retentionText } from "../lib/policy";
import { LoadingRows, PageHead, Panel } from "../ui/Common";
import { useToast } from "../ui/Toasts";
import { policiesMessages } from "./policies.i18n";

const EMPTY: PolicyInput = {
  name: "",
  include: ["C:\\Users", "/Users"],
  exclude: ["*.tmp"],
  schedule: { frequency: "daily", time: "12:30", weekdays: [1, 2, 3, 4, 5], windowHours: 4 },
  retention: { daily: 14, weekly: 8, monthly: 12, yearly: 0 },
  rpoHours: 26,
  osSnapshot: true,
  bandwidthLimitMbps: null,
  image: { enabled: false, disks: [], exclude: {} },
};

// poolId não vai mais no corpo: o servidor escolhe o pool (ADR 0021).
function toInput(p: Policy): PolicyInput {
  return {
    name: p.name, include: p.include, exclude: p.exclude, schedule: p.schedule, retention: p.retention,
    rpoHours: p.rpoHours, osSnapshot: p.osSnapshot, bandwidthLimitMbps: p.bandwidthLimitMbps, image: p.image,
  };
}

export function PolicyEditPage() {
  const { orgId = "", policyId = "" } = useParams();
  const isNew = policyId === "new";
  const policies = usePolicies(orgId);
  const existing = policies.data?.find((p) => p.id === policyId);
  if (!isNew && !existing) return <div className="panel"><LoadingRows rows={8} /></div>;
  return <PolicyForm key={policyId} orgId={orgId} policyId={policyId} existing={existing} initial={existing ? toInput(existing) : EMPTY} />;
}

function PolicyForm({ orgId, policyId, existing, initial }: { orgId: string; policyId: string; existing?: Policy; initial: PolicyInput }) {
  const isNew = !existing;
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const invalidate = useInvalidateOrg();
  const [form, setForm] = useState<PolicyInput>(initial);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const t = useT(policiesMessages);
  const tc = useT(common);

  const f = form;
  const set = <K extends keyof PolicyInput>(k: K, v: PolicyInput[K]) => setForm({ ...f, [k]: v });
  const nameError = touched && !f.name.trim() ? t.nameRequired : null;
  const retentionError = touched && f.retention.daily + f.retention.weekly + f.retention.monthly + f.retention.yearly === 0 ? t.retentionRequired : null;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!f.name.trim() || f.retention.daily + f.retention.weekly + f.retention.monthly + f.retention.yearly === 0) return;
    setBusy(true);
    try {
      if (isNew) await api.createPolicy(orgId, f);
      else await api.updatePolicy(orgId, policyId, f);
      invalidate(orgId);
      toast("ok", isNew ? t.created(f.name) : t.saved(f.name));
      navigate(`/o/${orgId}/policies`);
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : t.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  const lines = (v: string) => v.split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <form onSubmit={save} style={{ display: "grid", gap: 20 }} noValidate>
      <PageHead
        eyebrow={t.eyebrow}
        title={isNew ? t.newPolicy : existing?.name ?? t.fallbackTitle}
        lede={existing ? t.followers(existing.machineCount) : t.newLede}
        actions={
          <>
            <Link className="btn" to={`/o/${orgId}/policies`}><ArrowLeft aria-hidden="true" /> {tc.cancel}</Link>
            <button className="btn btn-primary" type="submit" disabled={busy}><Save aria-hidden="true" /> {t.savePolicy}</button>
          </>
        }
      />

      <div className="grid-2">
        <Panel title={t.selection}>
          <div style={{ display: "grid", gap: 16 }}>
            <div className="field">
              <label htmlFor="pol-nome">{t.name}</label>
              <input id="pol-nome" className="input" value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={80} aria-invalid={Boolean(nameError)} />
              {nameError && <span className="error">{nameError}</span>}
            </div>
            <div className="field">
              <label htmlFor="pol-incluir">{t.include}</label>
              <textarea id="pol-incluir" className="textarea" value={f.include.join("\n")} onChange={(e) => set("include", lines(e.target.value))} />
              <span className="hint">{t.includeHint}</span>
            </div>
            <div className="field">
              <label htmlFor="pol-excluir">{t.exclude}</label>
              <textarea id="pol-excluir" className="textarea" value={f.exclude.join("\n")} onChange={(e) => set("exclude", lines(e.target.value))} />
              <span className="hint">{t.excludeHintPrefix} <code>**/AppData/Local/Temp</code></span>
            </div>
          </div>
        </Panel>

        <div style={{ display: "grid", gap: 20 }}>
          <Panel title={t.schedule}>
            <div style={{ display: "grid", gap: 16 }}>
              <div className="field">
                <span className="label" id="pol-freq">{t.frequency}</span>
                <div className="segmented" role="group" aria-labelledby="pol-freq">
                  {(["hourly", "daily", "weekly"] as const).map((fr) => (
                    <button key={fr} type="button" aria-pressed={f.schedule.frequency === fr} onClick={() => set("schedule", { ...f.schedule, frequency: fr })}>
                      {t.frequencyOption[fr]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="pol-hora">{f.schedule.frequency === "hourly" ? t.startMinute : t.time}</label>
                  <input id="pol-hora" className="input num" type="time" value={f.schedule.time} onChange={(e) => set("schedule", { ...f.schedule, time: e.target.value })} />
                </div>
                <div className="field">
                  <label htmlFor="pol-janela">{t.windowHours}</label>
                  <input id="pol-janela" className="input num" type="number" min={1} max={24} value={f.schedule.windowHours} onChange={(e) => set("schedule", { ...f.schedule, windowHours: Number(e.target.value) })} />
                </div>
                <div className="field">
                  <label htmlFor="pol-rpo">{t.rpoHours}</label>
                  <input id="pol-rpo" className="input num" type="number" min={1} value={f.rpoHours} onChange={(e) => set("rpoHours", Number(e.target.value))} />
                  <span className="hint">{t.rpoHint}</span>
                </div>
              </div>
              {f.schedule.frequency !== "hourly" && (
                <div className="field">
                  <span className="label" id="pol-dias">{t.days}</span>
                  <div className="segmented" role="group" aria-labelledby="pol-dias">
                    {tc.weekdaysShort.map((d, i) => {
                      const on = f.schedule.weekdays.includes(i);
                      return (
                        <button key={d} type="button" aria-pressed={on}
                          onClick={() => set("schedule", { ...f.schedule, weekdays: on ? f.schedule.weekdays.filter((x) => x !== i) : [...f.schedule.weekdays, i].sort() })}>
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Panel>

          <Panel title={t.retention} sub={t.retentionSummary(retentionReach(tc, f.retention), retentionText(tc, f.retention))}>
            <div style={{ display: "grid", gap: 10 }}>
              <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))" }}>
                {(["daily", "weekly", "monthly", "yearly"] as const).map((k) => (
                  <div className="field" key={k}>
                    <label htmlFor={`pol-ret-${k}`}>{t.retentionField[k]}</label>
                    <input id={`pol-ret-${k}`} className="input num" type="number" min={0} value={f.retention[k]} onChange={(e) => set("retention", { ...f.retention, [k]: Math.max(0, Number(e.target.value)) })} />
                  </div>
                ))}
              </div>
              {retentionError && <span className="field error">{retentionError}</span>}
              <span className="hint muted" style={{ fontSize: "var(--fs-12)" }}>{t.retentionHint}</span>
            </div>
          </Panel>

          <Panel title={t.destination}>
            <div style={{ display: "grid", gap: 16 }}>
              {/* O destino é escolhido pelo servidor entre as áreas contratadas (ADR 0021). */}
              <div className="callout"><Info aria-hidden="true" /><span>{t.poolAutomatic}</span></div>
              <label className="check" htmlFor="pol-snapshot">
                <input id="pol-snapshot" type="checkbox" checked={f.osSnapshot} onChange={(e) => set("osSnapshot", e.target.checked)} />
                <span><strong>{t.osSnapshot}</strong><br /><span className="ink-2">{t.osSnapshotHint}</span></span>
              </label>
              <div className="field">
                <label htmlFor="pol-banda">{t.bandwidth}</label>
                <input id="pol-banda" className="input num" type="number" min={1} placeholder={t.noLimit} value={f.bandwidthLimitMbps ?? ""} onChange={(e) => set("bandwidthLimitMbps", e.target.value ? Number(e.target.value) : null)} />
              </div>
            </div>
          </Panel>

          {/* Imagem de disco (ADR 0022): o custo e a consistência ficam à vista
              antes de ligar, não escondidos. */}
          <Panel title={t.image}>
            <div style={{ display: "grid", gap: 16 }}>
              <label className="check" htmlFor="pol-imagem">
                <input id="pol-imagem" type="checkbox" checked={f.image.enabled}
                  onChange={(e) => set("image", { ...f.image, enabled: e.target.checked })} />
                <span><strong>{t.imageEnable}</strong><br /><span className="ink-2">{t.imageEnableHint}</span></span>
              </label>
              {f.image.enabled && (
                <>
                  <div className="callout warn" role="status"><TriangleAlert aria-hidden="true" /><span>{t.imageStorage}</span></div>
                  <div className="callout"><Info aria-hidden="true" /><span>{t.imageConsistency}</span></div>
                  <div className="callout"><Info aria-hidden="true" /><span>{t.imageMac}</span></div>
                  <div className="field">
                    <label htmlFor="pol-imagem-discos">{t.imageDisks}</label>
                    <textarea id="pol-imagem-discos" className="textarea" rows={2} value={f.image.disks.join("\n")}
                      onChange={(e) => set("image", { ...f.image, disks: lines(e.target.value) })} />
                    <span className="hint">{t.imageDisksHint}</span>
                  </div>
                </>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </form>
  );
}
